import React from "react";

// Single per-session guard so we only ever force ONE automatic reload for a
// stale-deploy chunk failure. It is cleared on the first successful dynamic
// import after a reload, so a later deploy in the same session can recover too.
const RELOAD_GUARD_KEY = "app:chunkReloadGuard";

function readReloadGuard(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
  } catch {
    return false;
  }
}

function setReloadGuard(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    /* sessionStorage may be unavailable (private mode, etc.) */
  }
}

function clearReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    /* no-op */
  }
}

/**
 * Performs a single, session-guarded full-page reload to recover from a stale
 * deploy. Returns true if a reload was triggered; false if the guard already
 * fired this session (so callers can surface the error instead of looping).
 */
function guardedReload(): boolean {
  if (readReloadGuard()) return false;
  setReloadGuard();
  window.location.reload();
  return true;
}

/**
 * Detects the family of errors thrown when a hashed code-split chunk can no
 * longer be fetched. This happens after a fresh deploy replaces the hashed
 * asset filenames while a client still holds a cached index.html that points at
 * the old chunk: the server returns index.html (text/html) for the missing
 * `.js`, so the dynamic import rejects with a MIME / "failed to fetch" error.
 */
function isChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error ? error.message : String(error ?? "");
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Expected a JavaScript(-or-Wasm)? module script/i.test(message) ||
    /MIME type of ['"]?text\/html/i.test(message) ||
    // Safari/WebKit wording, e.g. "'text/html' is not a valid JavaScript MIME type".
    /is not a valid JavaScript MIME type/i.test(message) ||
    /ChunkLoadError/i.test(message)
  );
}

/**
 * Drop-in replacement for `React.lazy` that adds resilience against transient
 * network failures and stale-deploy chunk errors.
 *
 * Behaviour:
 * 1. Try the import. On success, clear the reload guard.
 * 2. On failure, retry once after a short delay (handles transient blips).
 * 3. If it still fails with a chunk-load error, force a single full-page reload
 *    to pull the fresh index.html and its new asset references. A sessionStorage
 *    guard prevents an infinite reload loop if the asset is genuinely missing.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  options?: { retryDelayMs?: number },
): React.LazyExoticComponent<T> {
  const retryDelayMs = options?.retryDelayMs ?? 400;

  return React.lazy(async () => {
    try {
      const mod = await factory();
      clearReloadGuard();
      return mod;
    } catch (firstError) {
      // One retry for transient network errors before considering a reload.
      try {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        const mod = await factory();
        clearReloadGuard();
        return mod;
      } catch (retryError) {
        const looksLikeStaleDeploy =
          isChunkLoadError(retryError) || isChunkLoadError(firstError);

        if (looksLikeStaleDeploy && guardedReload()) {
          // Keep showing the Suspense fallback while the page reloads instead
          // of surfacing the error to React.
          return new Promise<{ default: T }>(() => {});
        }

        throw retryError;
      }
    }
  });
}

/**
 * Registers a `vite:preloadError` listener as a build-native fallback. Vite
 * fires this event on `window` when a dynamically imported chunk fails to load
 * (regardless of the browser's exact error wording) before it throws. We
 * prevent that throw only when we run the same guarded single reload as the
 * import catch path above. If the guard has already fired, the error must
 * propagate so React can surface it instead of leaving the lazy import broken.
 * Call once at app startup.
 */
export function installChunkErrorReloadHandler(): void {
  window.addEventListener("vite:preloadError", (event) => {
    if (guardedReload()) {
      event.preventDefault();
    }
  });
}
